import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const [skipped, setSkipped] = useState(false);
  const skippedRef = useRef(false);

  const check = useCallback(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false));
  }, []);

  // Called by protected routes: if the initial check was skipped on a public page
  // (e.g. the visitor then clicked through to the console), run it now.
  const ensureChecked = useCallback(() => {
    if (!skippedRef.current) return;
    skippedRef.current = false;
    setSkipped(false);
    setUser(null);
    check();
  }, [check]);

  useEffect(() => {
    // Anonymous visitors on public pages don't need a session check; skipping it
    // avoids a guaranteed 401 in the console and one wasted request on the landing page.
    const p = window.location.pathname;
    const needsAuth = p.startsWith("/console") || p === "/login" || p === "/register";
    if (!needsAuth) { skippedRef.current = true; setSkipped(true); setUser(false); return; }
    check();
  }, [check]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, logout, ensureChecked, skipped }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
